import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import useDebounce from "../utils/useDebounce";
import useAuth from "./useAuth";
import API from "../utils/Axios.utils";

const useJobManagement = (configOptions = {}) => {
    const {
        fetchUrlBase,
        dataKey = "jobPost",
        initialPageSize = 10,
        syncWithUrl = true,
        extraParams = {},
        resetOnParamsChange = false,
    } = configOptions;

    const navigate = useNavigate();
    const location = useLocation();
    const { token } = useAuth();

    const queryParams = new URLSearchParams(location.search);
    const initialPage = parseInt(queryParams.get("page") || "1", 10);
    const initialSearch = queryParams.get("search") || "";

    const [page, setPage] = useState(initialPage);
    const [searchText, setSearchText] = useState(initialSearch);
    const debouncedSearchText = useDebounce(searchText, 500);

    useEffect(() => {
        if (syncWithUrl) {
            const q = new URLSearchParams(location.search);
            const p = parseInt(q.get("page") || "1", 10);
            const s = q.get("search") || "";
            setPage(p);
            if (s !== searchText) {
                setSearchText(s);
            }
        }
    }, [location.search, syncWithUrl, searchText]);

    // Snackbar/Notification
    const [alert, setAlert] = useState(false);
    const [alertContent, setAlertContent] = useState("");
    const [alertType, setAlertType] = useState("");

    const isFirstRun = useRef(true);
    const extraParamsString = JSON.stringify(extraParams);

    useEffect(() => {
        if (resetOnParamsChange) {
            if (isFirstRun.current) {
                isFirstRun.current = false;
                return;
            }

            // Check if current URL already has these params to avoid redundant resets
            const currentParams = new URLSearchParams(location.search);
            const currentSearch = currentParams.get("search") || "";

            // If the params are different from what's in the URL, then reset to page 1
            let paramsChanged = debouncedSearchText !== currentSearch;

            if (!paramsChanged) {
                Object.keys(extraParams).forEach((key) => {
                    const value = extraParams[key];
                    if (currentParams.has(key)) {
                        const urlValue = currentParams.get(key) || "";
                        if (value !== undefined && value !== null && String(value) !== urlValue) {
                            paramsChanged = true;
                        }
                    }
                });
            }

            if (paramsChanged) {
                setPage(1);
                if (syncWithUrl) {
                    const newParams = new URLSearchParams(location.search);
                    newParams.set("page", "1");
                    navigate(`?${newParams.toString()}`, { replace: true });
                }
            }
        }
    }, [debouncedSearchText, extraParamsString, resetOnParamsChange, syncWithUrl, navigate, location.search]);

    // React Query: Define the query function
    const fetchJobs = async () => {
        if (!fetchUrlBase) return { data: [], totalCount: 0 };

        let url = `${fetchUrlBase}${fetchUrlBase.includes("?") ? "&" : "?"}page=${page}&pageSize=${initialPageSize}`;

        if (debouncedSearchText) {
            url += `&search=${encodeURIComponent(debouncedSearchText.trim())}`;
        }

        Object.keys(extraParams).forEach((key) => {
            const value = extraParams[key];
            if (value !== undefined && value !== "" && value !== null) {
                url += `&${key}=${encodeURIComponent(value)}`;
            }
        });

        // const token = JSON.parse(localStorage.getItem("userLoggedIn"))?.token;
        // replaced by useAuth hook usage above
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };

        const res = await API.get(url, config);

        // Handle the case where the backend might return the data in different keys
        const data = res?.data?.[dataKey] || res?.data?.jobPost || res?.data?.job || res?.data?.job_post || [];
        const totalCount = res?.data?.totalCount || 0;

        return { data, totalCount };
    };

    // React Query: Use the query
    const queryKey = ['jobs', fetchUrlBase, page, debouncedSearchText, extraParams];

    const {
        data: queryData,
        isLoading,
        isFetching,
        error: queryError,
        refetch,
    } = useQuery({
        queryKey,
        queryFn: fetchJobs,
        enabled: !!fetchUrlBase, // Only run if fetchUrlBase is provided
        placeholderData: (previousData) => previousData, // Equivalent to keepPreviousData: true in v5
    });

    useEffect(() => {
        if (queryError) {
            setAlertContent(queryError?.response?.data?.message || "Failed to fetch jobs");
            setAlertType("error");
            setAlert(true);
        }
    }, [queryError]);

    const jobData = queryData?.data || [];
    const listCount = queryData?.totalCount || 0;

    const handlePageChange = (e, value) => {
        if (syncWithUrl) {
            const newParams = new URLSearchParams(location.search);
            newParams.set("page", value);
            navigate(`?${newParams.toString()}`);
        } else {
            setPage(value);
        }
    };

    const handleAlertClose = () => {
        setAlert(false);
    };

    const forceUpdate = () => {
        refetch();
    };

    return {
        page,
        setPage,
        listCount,
        setListCount: () => { }, // No-op, React Query manages this
        handlePageChange,
        searchText,
        setSearchText,
        showLoader: isLoading,
        setShowLoader: () => { }, // No-op, React Query manages this
        jobData,
        setJobData: () => { }, // No-op, React Query manages this
        alert,
        alertContent,
        alertType,
        setAlert,
        setAlertContent,
        setAlertType,
        handleAlertClose,
        forceUpdate,
        fetchJobs: refetch,
        isLoading,
        isFetching,
        error: queryError,
    };
};

export default useJobManagement;
